import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutGrid, List, Loader2, Puzzle } from "lucide-react";
import { useTools } from "../../application/useMarketplace";
import { MarketplaceFilters } from "../../domain/types";
import ToolCard from "../components/ToolCard";
import ToolFilters from "../components/ToolFilters";
import ToolRow from "../components/ToolRow";

// Cards to browse what each tool is, or a list to scan the whole catalogue.
type Layout = "cards" | "list";

const LAYOUT_KEY = "alchely.toolsCatalog.layout";

/** The layout this therapist last chose; cards if they never chose. */
function readStoredLayout(): Layout {
    try {
        return localStorage.getItem(LAYOUT_KEY) === "list" ? "list" : "cards";
    } catch {
        // A browser that refuses storage gets the cards.
        return "cards";
    }
}

const MarketplacePage: React.FC = () => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState<MarketplaceFilters>({});
    const { data: tools, isLoading, error } = useTools(filters);
    const [layout, setLayout] = useState<Layout>(readStoredLayout);
    const selectLayout = (next: Layout) => {
        setLayout(next);
        try {
            localStorage.setItem(LAYOUT_KEY, next);
        } catch {
            // Still switches; it just starts on cards next session.
        }
    };
    const layoutClasses = (value: Layout) =>
        `flex size-9 items-center justify-center rounded-md transition-colors ${
            layout === value
                ? "bg-surface-raised text-accent shadow-theme-xs"
                : "text-content-tertiary hover:text-content-secondary"
        }`;

    // An empty catalogue and an empty result set are different problems, and
    // telling someone no tools exist when they have just filtered them out
    // sends them looking for a fault that is not there.
    const isFiltered = Object.values(filters).some(Boolean);

    return (
        <div className="mx-auto max-w-7xl px-4 py-6">
            <header className="mb-6 flex items-center gap-3">
                <Puzzle className="text-brand-500" size={24} />
                <div>
                    <h1 className="text-xl font-semibold text-content-primary">
                        {t("marketplace.title")}
                    </h1>
                    <p className="text-sm text-content-tertiary">
                        {t("marketplace.subtitle")}
                    </p>
                </div>
            </header>

            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <ToolFilters filters={filters} onChange={setFilters} />
                <div
                    role="radiogroup"
                    aria-label={t("marketplace.layout.label")}
                    className="flex w-fit gap-0.5 rounded-lg bg-surface-sunken p-0.5"
                >
                    {(
                        [
                            ["cards", LayoutGrid, "marketplace.layout.cards"],
                            ["list", List, "marketplace.layout.list"],
                        ] as [Layout, typeof List, string][]
                    ).map(([value, Icon, labelKey]) => (
                        <button
                            key={value}
                            type="button"
                            role="radio"
                            aria-checked={layout === value}
                            aria-label={t(labelKey)}
                            title={t(labelKey)}
                            className={layoutClasses(value)}
                            onClick={() => selectLayout(value)}
                        >
                            <Icon size={16} aria-hidden="true" />
                        </button>
                    ))}
                </div>
            </div>

            {isLoading && (
                <div className="flex items-center justify-center py-16 text-content-tertiary">
                    <Loader2 className="animate-spin" size={28} />
                </div>
            )}

            {error && !isLoading && (
                <div className="rounded-xl border border-error-200 bg-error-50 p-6 text-center text-sm text-error-600 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
                    {t("marketplace.errorLoading")}
                </div>
            )}

            {!isLoading && !error && tools && tools.length === 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-10 text-center text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400">
                    {isFiltered ? (
                        <>
                            <p>{t("marketplace.emptyFiltered")}</p>
                            <button
                                type="button"
                                onClick={() => setFilters({})}
                                className="mt-3 text-sm font-medium text-brand-600 underline dark:text-brand-400"
                            >
                                {t("marketplace.clearFilters")}
                            </button>
                        </>
                    ) : (
                        t("marketplace.empty")
                    )}
                </div>
            )}

            {!isLoading && !error && tools && tools.length > 0 && layout === "cards" && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {tools.map((tool) => (
                        <ToolCard key={tool.id} tool={tool} />
                    ))}
                </div>
            )}

            {!isLoading && !error && tools && tools.length > 0 && layout === "list" && (
                <ul className="flex flex-col gap-2">
                    {tools.map((tool) => (
                        <li key={tool.id}>
                            <ToolRow tool={tool} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default MarketplacePage;
